import { useState, useEffect, useRef } from 'react'
import { useSearchParams, useNavigate, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { supabase } from '../lib/supabase'

export default function AuthConfirm() {
  const { t } = useTranslation('auth')
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [status, setStatus] = useState('verifying') // verifying | success | error
  const [errorMessage, setErrorMessage] = useState('')
  const attempted = useRef(false)

  const tokenHash = searchParams.get('token_hash')
  const type = searchParams.get('type')

  useEffect(() => {
    if (attempted.current) return
    attempted.current = true

    if (!tokenHash || !type) {
      setStatus('error')
      setErrorMessage(t('authConfirm.invalidLink'))
      return
    }

    async function verify() {
      try {
        const { data, error } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type })
        if (error) {
          setStatus('error')
          setErrorMessage(error.message)
          return
        }

        setStatus('success')

        // Redirect based on user role after brief delay
        const role = data?.user?.app_metadata?.role
        setTimeout(() => {
          if (role === 'facilitator') {
            navigate('/facilitator/profile', { replace: true })
          } else if (role === 'admin') {
            navigate('/admin', { replace: true })
          } else {
            navigate('/login', { replace: true })
          }
        }, 2000)
      } catch (err) {
        setStatus('error')
        setErrorMessage(err.message || t('authConfirm.verificationFailed'))
      }
    }

    verify()
  }, [tokenHash, type, navigate, t])

  return (
    <div className="min-h-screen flex items-center justify-center px-5">
      <div className="w-full max-w-md animate-fadeIn">
        <div className="text-center mb-8">
          <h1 className="font-display text-3xl font-medium text-gray-800 mb-2">
            {t('authConfirm.title')}
          </h1>
          <p className="text-gray-500">{t('authConfirm.subtitle')}</p>
        </div>

        <div className="bg-white rounded-xl p-8 shadow-sm text-center">
          {status === 'verifying' && (
            <p className="text-gray-600">{t('authConfirm.verifying')}</p>
          )}

          {status === 'success' && (
            <>
              <svg className="w-12 h-12 text-green-500 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-green-700 font-semibold">{t('authConfirm.success')}</p>
              <p className="text-sm text-gray-500 mt-2">{t('authConfirm.redirecting')}</p>
            </>
          )}

          {status === 'error' && (
            <>
              <svg className="w-12 h-12 text-red-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-red-700 font-semibold mb-2">{t('authConfirm.failedTitle')}</p>
              <p className="text-sm text-gray-500 mb-4">{errorMessage}</p>
              <Link
                to="/login"
                className="text-primary-600 text-sm font-semibold hover:underline"
              >
                {t('authConfirm.goToLogin')}
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
